import { config } from '../appConfig'
import { BTCNFT, Chain } from '../interfaces/common'
import { UTXO, UTXOChain } from '../interfaces/walletActions'
import { logger } from '../logger'

export class UTXOFetchError extends Error {
  constructor(
    public readonly chain: UTXOChain,
    public readonly cause: string
  ) {
    super(`Failed to fetch ${chain.toUpperCase()} UTXOs: ${cause}`)
    this.name = 'UTXOFetchError'
  }
}

export class InsufficientSpendableError extends Error {
  constructor(
    public readonly chain: UTXOChain,
    public readonly spendable: bigint,
    public readonly required: bigint,
    public readonly lockedInInscriptions: bigint
  ) {
    super(
      `Insufficient spendable ${chain.toUpperCase()}: spendable=${spendable}, required=${required}, locked=${lockedInInscriptions}`
    )
    this.name = 'InsufficientSpendableError'
  }
}

interface NetworkConfig {
  tatumCoin: string
  tatumDataChain: string
  symbol: string
}

const isTestnet = () => config.fetchEnv('BITCOIN_CHAIN') === 'testnet'

const NETWORK_CONFIGS: Record<UTXOChain, NetworkConfig> = {
  [Chain.BTC]: {
    tatumCoin: 'bitcoin',
    tatumDataChain: isTestnet() ? 'bitcoin-testnet' : 'bitcoin-mainnet',
    symbol: 'BTC',
  },
  [Chain.DOGE]: {
    tatumCoin: 'dogecoin',
    tatumDataChain: isTestnet() ? 'doge-testnet' : 'doge-mainnet',
    symbol: 'DOGE',
  },
  [Chain.LTC]: {
    tatumCoin: 'litecoin',
    tatumDataChain: isTestnet() ? 'litecoin-testnet' : 'litecoin-mainnet',
    symbol: 'LTC',
  },
}

const TATUM_API_BASE = 'https://api.tatum.io/v3'

interface TatumBalanceResponse {
  incoming: string
  outgoing: string
  incomingPending: string
  outgoingPending: string
}

interface TatumUTXO {
  txHash: string
  index: number
  value: number
  valueAsString: string
  address: string
  chain: string
}

interface TatumFeeResponse {
  fast: number
  medium: number
  slow: number
}

interface TransactionResponse {
  txId: string
}

class TatumClient {
  private static getApiKey(): string {
    const keyName = isTestnet() ? 'TATUM_API_KEY_TESTNET' : 'TATUM_API_KEY_MAINNET'
    return config.fetchEnv(keyName)
  }

  private static getNetworkConfig(chain: UTXOChain): NetworkConfig {
    return NETWORK_CONFIGS[chain]
  }

  private static async tatumRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    const apiKey = this.getApiKey()
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(apiKey && { 'x-api-key': apiKey }),
      ...options.headers,
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      const errorText = await response.text()
      logger.error('Tatum API error', { status: response.status, body: errorText })
      throw new Error(`Tatum API error: ${response.status}`)
    }

    return response.json() as Promise<T>
  }

  private static async fetchBalance(
    address: string,
    chain: UTXOChain
  ): Promise<TatumBalanceResponse> {
    const { tatumCoin } = this.getNetworkConfig(chain)
    try {
      return await this.tatumRequest<TatumBalanceResponse>(
        `${TATUM_API_BASE}/${tatumCoin}/address/balance/${address}`
      )
    } catch (e) {
      logger.error('Tatum getBalance request failed', { originalError: e })
      throw new UTXOFetchError(chain, (e as Error)?.message || 'balance request failed')
    }
  }

  static async getUTXOs(address: string, chain: UTXOChain = Chain.BTC): Promise<UTXO[]> {
    const { tatumDataChain } = this.getNetworkConfig(chain)
    const { incoming, outgoing } = await this.fetchBalance(address, chain)

    // No confirmed balance → no spendable UTXOs; skip the lookup entirely.
    if (parseFloat(incoming) - parseFloat(outgoing) <= 0) return []

    // `/data/utxos` returns enough UTXOs to cover `totalValue`. `incoming` (total ever
    // received, in whole coins) is always >= the current balance, so it guarantees the
    // full UTXO set is returned without a chain-specific magic ceiling — and being a raw
    // API string it carries no float-subtraction rounding risk.
    let response: TatumUTXO[]
    try {
      response = await this.tatumRequest<TatumUTXO[]>(
        `${TATUM_API_BASE}/data/utxos?chain=${tatumDataChain}&address=${address}&totalValue=${incoming}`
      )
    } catch (e) {
      logger.error('Tatum getUTXOs request failed', { originalError: e })
      throw new UTXOFetchError(chain, (e as Error)?.message || 'request failed')
    }

    // The Data API exposes neither block height nor scriptPubKey, and the helper
    // canister needs neither: it builds inputs from the outpoint + value and derives
    // the scriptPubKey from the wallet's own Taproot key (it never reads `height` or
    // `script`). Local size estimation assumes a P2TR witness for the same reason.
    return response.map((utxo) => ({
      mintTxid: utxo.txHash,
      mintIndex: utxo.index,
      mintHeight: 0,
      script: '',
      value: Math.round(parseFloat(utxo.valueAsString) * 1e8),
      address: utxo.address,
    }))
  }

  // Split UTXOs into spendable (no inscription on them) and inscription-locked.
  // Returns spendable=utxos and locked=[] for non-BTC chains and when no ordinals are reported.
  static splitOrdinalUtxos(
    utxos: UTXO[],
    userOrdinals: BTCNFT[],
    chain: UTXOChain
  ): { spendable: UTXO[]; locked: UTXO[] } {
    if (chain !== Chain.BTC || userOrdinals.length === 0) {
      return { spendable: utxos, locked: [] }
    }
    const ordinalKeys = new Set(userOrdinals.map((o) => `${o.txId}:${o.mintIndex}`))
    const spendable: UTXO[] = []
    const locked: UTXO[] = []
    for (const utxo of utxos) {
      if (ordinalKeys.has(`${utxo.mintTxid}:${utxo.mintIndex}`)) {
        locked.push(utxo)
      } else {
        spendable.push(utxo)
      }
    }
    return { spendable, locked }
  }

  static async getBalance(address: string, chain: UTXOChain = Chain.BTC): Promise<bigint> {
    const { incoming, outgoing } = await this.fetchBalance(address, chain)
    // Confirmed balance = incoming - outgoing (whole coins); convert to base units.
    return BigInt(Math.round((parseFloat(incoming) - parseFloat(outgoing)) * 1e8))
  }

  static async getChainFee(chain: UTXOChain = Chain.BTC): Promise<bigint> {
    const { symbol } = this.getNetworkConfig(chain)

    let response: TatumFeeResponse
    try {
      response = await this.tatumRequest<TatumFeeResponse>(
        `${TATUM_API_BASE}/blockchain/fee/${symbol}`
      )
    } catch (e) {
      logger.error('Tatum getChainFee request failed', { originalError: e })
      throw new UTXOFetchError(chain, (e as Error)?.message || 'fee request failed')
    }

    // Tatum returns fast/medium/slow fee rates in sat/vB (koinu/byte for DOGE).
    return BigInt(Math.ceil(response.medium))
  }

  private static selectUTXOs(utxos: UTXO[], requiredSats: bigint, excludeOrdinal?: UTXO): UTXO[] {
    const candidates = excludeOrdinal
      ? utxos.filter(
          (u) => u.mintTxid !== excludeOrdinal.mintTxid || u.mintIndex !== excludeOrdinal.mintIndex
        )
      : utxos

    return candidates
      .sort((a, b) => b.value - a.value)
      .reduce(
        (selected, utxo) => {
          if (selected.total < requiredSats) {
            selected.utxos.push(utxo)
            selected.total += utxo.value
          }
          return selected
        },
        { utxos: [] as UTXO[], total: 0 }
      ).utxos
  }

  // Estimate transaction size for legacy P2PKH transactions (DOGE, LTC)
  private static estimateLegacyTxSize(inputCount: number, outputCount: number): number {
    // Legacy P2PKH transaction size estimation:
    // - Version: 4 bytes
    // - Input count varint: 1-3 bytes (1 for < 253 inputs)
    // - Each input: 32 (txid) + 4 (vout) + 1 (scriptSig length) + ~107 (scriptSig) + 4 (sequence) = ~148 bytes
    // - Output count varint: 1-3 bytes
    // - Each output: 8 (value) + 1 (scriptPubKey length) + 25 (P2PKH scriptPubKey) = 34 bytes
    // - Locktime: 4 bytes
    const baseSize = 10 // version + locktime + varints
    const inputSize = 148 // P2PKH input with signature
    const outputSize = 34 // P2PKH output

    return baseSize + inputCount * inputSize + outputCount * outputSize
  }

  // Estimate SegWit transaction vsize for BTC
  // vsize = (base_size * 3 + total_size) / 4, rounded up
  // For SegWit: base_size excludes witness, total_size includes witness
  private static estimateSegwitTxVsize(inputUtxos: UTXO[], outputCount: number): number {
    // Base transaction (non-witness):
    // - Version: 4 bytes
    // - Input count varint: 1 byte (for < 253 inputs)
    // - Each input: 32 (txid) + 4 (vout) + 1 (empty scriptSig) + 4 (sequence) = 41 bytes
    // - Output count varint: 1 byte
    // - Each output: 8 (value) + 1 (length) + scriptPubKey. We don't know the recipient's
    //   address type here, so we use the largest standard output — P2TR/P2WSH at 43 bytes
    //   (34-byte scriptPubKey) — to avoid ever under-estimating the fee. The change output
    //   is always P2TR (the wallet's own address), so 43 is exact for it regardless.
    // - Locktime: 4 bytes
    const baseSize = 4 + 1 + inputUtxos.length * 41 + 1 + outputCount * 43 + 4

    // Witness data (marker + flag = 2 bytes, counted in total only).
    // Plug only generates Taproot (P2TR) addresses, so every input spends a P2TR UTXO:
    // witness per input = 1 (items count) + 1 (sig len) + 64 (schnorr sig) = 66 bytes.
    const witnessSize = 2 + inputUtxos.length * 66

    const totalSize = baseSize + witnessSize
    // vsize formula: ceil((base_size * 3 + total_size) / 4)
    return Math.ceil((baseSize * 3 + totalSize) / 4)
  }

  // Estimate transaction size for BTC (supports SegWit and Taproot)
  private static getTxSize(
    utxos: UTXO[],
    satoshiAmount: bigint,
    fee: bigint,
    chain: UTXOChain = Chain.BTC,
    ordinalUtxo?: UTXO
  ) {
    const { symbol } = this.getNetworkConfig(chain)
    const inputUtxos = this.selectUTXOs(utxos, satoshiAmount + fee, ordinalUtxo)

    let totalInput = 0n
    for (const utxo of inputUtxos) {
      totalInput += BigInt(utxo.value)
    }
    if (ordinalUtxo && chain === Chain.BTC) {
      totalInput += BigInt(ordinalUtxo.value)
    }

    const changeAmount = totalInput - satoshiAmount - fee
    if (changeAmount < 0) {
      throw new Error(`Not enough ${symbol} for tx with fee`)
    }

    // For DOGE/LTC (legacy chains), estimate size without building transaction
    if (chain === Chain.DOGE || chain === Chain.LTC) {
      const inputCount = inputUtxos.length
      const outputCount = changeAmount > 0n ? 2 : 1 // recipient + optional change
      const estimatedSize = this.estimateLegacyTxSize(inputCount, outputCount)
      return BigInt(estimatedSize)
    }

    // For BTC, calculate vsize based on input/output structure
    const allInputUtxos = ordinalUtxo ? [ordinalUtxo, ...inputUtxos] : inputUtxos
    const outputCount = 1 + (changeAmount > 0n ? 1 : 0)
    const estimatedSize = this.estimateSegwitTxVsize(allInputUtxos, outputCount)

    return BigInt(estimatedSize)
  }

  static estimateFee(
    utxos: UTXO[],
    userOrdinals: BTCNFT[],
    walletAddress: string,
    amount: bigint,
    feePerByte: bigint,
    chain: UTXOChain = Chain.BTC,
    ordinal?: BTCNFT
  ): bigint {
    let totalFee = 0n
    const { symbol } = this.getNetworkConfig(chain)

    const { spendable: filteredUtxos, locked } = this.splitOrdinalUtxos(utxos, userOrdinals, chain)
    const spendableTotal = filteredUtxos.reduce((sum, u) => sum + BigInt(u.value), 0n)
    const lockedTotal = locked.reduce((sum, u) => sum + BigInt(u.value), 0n)

    const ordinalUtxo =
      ordinal && chain === Chain.BTC
        ? utxos.find((u) => u.mintTxid === ordinal.txId && u.mintIndex === ordinal.mintIndex)
        : undefined

    const MAX_ITERATIONS = 20
    for (let i = 0; i < MAX_ITERATIONS; i++) {
      try {
        const transactionSize = this.getTxSize(filteredUtxos, amount, totalFee, chain, ordinalUtxo)
        const newFee = transactionSize * feePerByte
        if (newFee === totalFee) return totalFee
        totalFee = newFee
      } catch (e) {
        if ((e as Error).message === `Not enough ${symbol} for tx with fee`) {
          // Max-send fallback: if a normal tx with a change output won't fit,
          // try spending all available inputs into a single output (no change),
          // donating any excess to fee. This is what happens when the user sends
          // an amount close to their total balance: the change would be small enough
          // that adding it pushes fees past what's affordable. Skipped when sending
          // an ordinal, since ordinal sends have a fixed output structure.
          if (!ordinalUtxo && filteredUtxos.length > 0) {
            const noChangeVsize =
              chain === Chain.DOGE || chain === Chain.LTC
                ? this.estimateLegacyTxSize(filteredUtxos.length, 1)
                : this.estimateSegwitTxVsize(filteredUtxos, 1)
            const noChangeFee = BigInt(noChangeVsize) * feePerByte
            if (spendableTotal >= amount + noChangeFee) {
              return spendableTotal - amount
            }
          }
          throw new InsufficientSpendableError(
            chain,
            spendableTotal,
            amount + totalFee,
            lockedTotal
          )
        }
        logger.debug(`${symbol} estimateFee error`, { originalError: e })
        throw e
      }
    }

    logger.error(`${symbol} estimateFee did not converge after ${MAX_ITERATIONS} iterations`, {
      amount: amount.toString(),
      totalFee: totalFee.toString(),
      spendableTotal: spendableTotal.toString(),
    })
    return totalFee
  }

  // Compute the maximum sendable amount for a UTXO chain: all spendable inputs,
  // 1 output (no change). No circular dependency on fee since the input/output
  // set is fixed once "send all" is chosen.
  static estimateMaxSendable(
    utxos: UTXO[],
    userOrdinals: BTCNFT[],
    feePerByte: bigint,
    chain: UTXOChain = Chain.BTC
  ): { maxAmount: bigint; fee: bigint; spendable: bigint; lockedInInscriptions: bigint } {
    const { spendable: filteredUtxos, locked } = this.splitOrdinalUtxos(utxos, userOrdinals, chain)
    const spendableTotal = filteredUtxos.reduce((sum, u) => sum + BigInt(u.value), 0n)
    const lockedTotal = locked.reduce((sum, u) => sum + BigInt(u.value), 0n)

    if (filteredUtxos.length === 0) {
      return { maxAmount: 0n, fee: 0n, spendable: 0n, lockedInInscriptions: lockedTotal }
    }

    const vsize =
      chain === Chain.DOGE || chain === Chain.LTC
        ? this.estimateLegacyTxSize(filteredUtxos.length, 1)
        : this.estimateSegwitTxVsize(filteredUtxos, 1)

    const fee = BigInt(vsize) * feePerByte
    const maxAmount = spendableTotal > fee ? spendableTotal - fee : 0n
    return { maxAmount, fee, spendable: spendableTotal, lockedInInscriptions: lockedTotal }
  }

  static async publishTransaction(
    rawTx: string,
    chain: UTXOChain = Chain.BTC
  ): Promise<{ txid: string }> {
    const { tatumCoin, symbol } = this.getNetworkConfig(chain)

    const handleError = (data: unknown) => {
      logger.error(`Failed to publish ${symbol} transaction`, data)
      throw new Error('Failed to publish transaction')
    }

    let result
    try {
      result = await this.tatumRequest<TransactionResponse>(
        `${TATUM_API_BASE}/${tatumCoin}/broadcast`,
        {
          method: 'POST',
          body: JSON.stringify({ txData: rawTx }),
        }
      )
    } catch (e) {
      return handleError({ originalError: e })
    }

    return { txid: result.txId }
  }
}

export default TatumClient
